import FocusTrap from 'focus-trap-react';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@foundation/utils/cn';

import { Portal } from './portals/Portal';
import { Typography } from './text';
import { CloseIconCircle } from '../icons';

export type ModalProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly styles?: {
    readonly container?: string;
    readonly header?: string;
    readonly modal?: string;
    readonly content?: string;
    readonly footer?: string;
  };
  readonly closePrevented?: boolean;
  readonly showCloseButton?: boolean;
  readonly disableDefaultAnimation?: boolean;
  readonly swipeDownToClose?: boolean;
  readonly initialFocus?: () => void;
  readonly returnFocus?: () => HTMLElement | SVGElement | string | false;
  readonly 'data-testid'?: string;
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  styles = {},
  showCloseButton = true,
  disableDefaultAnimation = false,
  swipeDownToClose = true,
  closePrevented = false,
  initialFocus,
  returnFocus,
  'data-testid': dataTestId,
}: ModalProps) {
  const {
    container: containerClass,
    header: headerClass,
    modal: modalClass,
    content: contentClass,
    footer: footerClass,
  } = styles;

  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setIsDragging(false);
      setDragOffset(0);
      setIsClosing(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    // Manual Escape key handler instead of using FocusTrap's escapeDeactivates option.
    // In React 18 Strict Mode, using onDeactivate to close the modal causes it to close
    // immediately during the mount/unmount cycle. This manual handler avoids that issue.
    // See: https://github.com/focus-trap/focus-trap-react?tab=readme-ov-file#%EF%B8%8F%EF%B8%8F-react-18-strict-mode-%EF%B8%8F%EF%B8%8F
    const handleEscape = (e: KeyboardEvent) => {
      if (closePrevented) return;
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closePrevented]);

  const handleClose = () => {
    // Only animate slide-down on mobile (where modal is a bottom sheet)
    const isMobile = window.innerWidth < 768; // md breakpoint

    if (isMobile) {
      // Start closing animation
      setIsClosing(true);
      // Animate to full height
      if (modalRef.current) {
        const modalHeight = modalRef.current.offsetHeight;
        setDragOffset(modalHeight);
      }
      // Trigger actual close after animation
      setTimeout(() => {
        onClose();
      }, 300);
    } else {
      // On desktop, close immediately (uses fade-out from CSS)
      onClose();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!swipeDownToClose || closePrevented) return;

    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !swipeDownToClose || closePrevented) return;

    currentY.current = e.touches[0].clientY;
    const offset = Math.max(0, currentY.current - startY.current); // Only allow downward drag
    setDragOffset(offset);
  };

  const handleTouchEnd = () => {
    if (!isDragging || !swipeDownToClose || closePrevented) return;

    const dragDistance = currentY.current - startY.current;

    setIsDragging(false);

    // If dragged down more than 100px, close the modal
    if (dragDistance > 100) {
      // Mark as closing and animate to full height
      setIsClosing(true);
      // Smoothly animate from current position to full screen height
      if (modalRef.current) {
        const modalHeight = modalRef.current.offsetHeight;
        setDragOffset(modalHeight);
      }
      // Trigger close after a short delay for smooth animation
      setTimeout(() => {
        onClose();
      }, 300);
    } else {
      // User didn't drag far enough, snap back to original position
      setDragOffset(0);
    }
  };

  const renderTitle = useCallback(() => {
    if (typeof title === 'string') {
      return (
        <Typography
          variant="h2"
          className="text-xl font-semibold "
        >
          {title}
        </Typography>
      );
    }
    return <>{title}</>;
  }, [title]);

  if (!isOpen) return null;

  return (
    <Portal id="pmi-modal-root">
      <FocusTrap
        focusTrapOptions={{
          initialFocus: initialFocus,
          setReturnFocus: returnFocus,
          allowOutsideClick: true,
          escapeDeactivates: false,
          fallbackFocus: () => document.body,
        }}
      >
        <div
          className={cn(
            'fixed inset-0 z-50 flex items-end md:items-center md:justify-center',
            containerClass,
          )}
        >
          <div
            className={cn('fixed inset-0 bg-overlay backdrop-blur-sm')}
            onClick={closePrevented ? undefined : handleClose}
            data-testid={dataTestId ? `${dataTestId}-overlay` : undefined}
          />

          <div
            ref={modalRef}
            style={{
              transform:
                isDragging || isClosing
                  ? `translateY(${dragOffset}px)`
                  : undefined,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            }}
            className={cn(
              'relative z-50 w-full bg-white overflow-hidden',
              'max-h-[90vh] rounded-t-md fl:rounded-t-2xl',
              'md:max-h-[85vh] md:max-w-[80%] md:rounded-md fl:md:rounded-2xl',
              'flex flex-col',
              !disableDefaultAnimation && 'animate-slide-up md:animate-fade-in',
              modalClass,
            )}
          >
            {/* Swipe indicator */}
            {swipeDownToClose && (
              <div
                className="absolute w-full flex justify-center py-2 md:hidden"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {!closePrevented && (
                  <div className="w-12 h-1 bg-gray-300 rounded-full" />
                )}
              </div>
            )}

            {(title || showCloseButton) && (
              <div
                onTouchStart={
                  swipeDownToClose && !closePrevented
                    ? handleTouchStart
                    : undefined
                }
                onTouchMove={
                  swipeDownToClose && !closePrevented
                    ? handleTouchMove
                    : undefined
                }
                onTouchEnd={
                  swipeDownToClose && !closePrevented
                    ? handleTouchEnd
                    : undefined
                }
                className={cn(
                  'flex items-center justify-between gap-2 px-5 py-3 text-primary min-h-16',
                  headerClass,
                )}
              >
                {title && renderTitle()}
                {showCloseButton && !closePrevented && (
                  <button
                    type="button"
                    onClick={handleClose}
                    className="py-0 text-gray-300 hover:text-gray-600 rounded-full transition-colors ml-auto cursor-pointer"
                    aria-label="Close"
                    data-testid={
                      dataTestId ? `${dataTestId}-close-button` : undefined
                    }
                  >
                    <CloseIconCircle
                      size={24}
                      stroke="var(--color-gray-800)"
                    />
                  </button>
                )}
              </div>
            )}

            <div className={cn('flex-1 overflow-y-auto', contentClass)}>
              {children}
            </div>

            {footer && (
              <div
                className={cn(
                  'sticky bottom-0 bg-white px-5 py-4',
                  footerClass,
                )}
              >
                {footer}
              </div>
            )}
          </div>
        </div>
      </FocusTrap>
    </Portal>
  );
}
